import { useConfigStore } from '../feature-flags/useConfigStore'
import { z } from 'zod'

const userSettingsSchema = z.object({
  position: z
    .union([z.literal('off'), z.literal('footer'), z.literal('navbar')])
    .default('off')
    .transform((value) => {
      if (value === 'navbar') return 'footer'
      return value
    }),
  defaultSettings: z.object({
    theme: z.union([z.literal('light'), z.literal('dark'), z.literal('auto')]),
    articleSummary: z.boolean().default(true),
  }),
  enabledSettings: z
    .object({
      articleSummary: z.boolean().default(false),
    })
    .default({ articleSummary: false }),
})

export const siteSettingsSchema = z.object({
  userSettings: z.optional(userSettingsSchema),
  // Avoid optional().default() chaining due to Zod inference quirks.
  // See: https://github.com/colinhacks/zod/issues/2491
  enableLocalNewsSearch: z.boolean().default(true),
  sortNewsBy: z
    .union([
      z.literal('publishedDate'), // Sorts by descending order of published date (by full date & time).
      z.literal('clusterSize'), // Sorts by descending order of published date (by day), with articles in each day additionally sorted by cluster size (descending order).
    ])
    .default('publishedDate'),

  // Master gate for feed reordering
  enableArticleReordering: z.boolean().default(false),

  // NEW: which status signal(s) should be used to derank items
  derankItemsByStatus: z
    .union([
      z.literal('none'), // no deranking, applies global freshness sort
      z.literal('viewed'), // derank based on feed visibility
      z.literal('read'), // derank based on actual read/open
      z.literal('both'), // derank when either viewed OR read
    ])
    .default('viewed'),

  videoEnabled: z.optional(z.boolean()),
  // enable games category tag
  gamesEnabled: z.optional(z.boolean()),
})

// Use output type for runtime-consumed shape (defaults applied)
export type SiteSettings = z.output<typeof siteSettingsSchema>
// Input type for authoring configs/mocks without requiring defaulted keys
export type SiteSettingsInput = z.input<typeof siteSettingsSchema>

export const useSiteSettingsStore = useConfigStore({
  defaults: siteSettingsSchema.parse({
    userSettings: {
      position: 'off',
      defaultSettings: {
        theme: 'light',
        articleSummary: true,
      },
      enabledSettings: {
        articleSummary: false,
      },
    },
    enableLocalNewsSearch: true,
    sortNewsBy: 'clusterSize',
    enableArticleReordering: false,
    derankItemsByStatus: 'viewed', // NEW default to preserve current A/B behavior
    videoEnabled: false,
    gamesEnabled: false,
  }),
  configStoreKey: 'siteSettings',
  schema: (data) => siteSettingsSchema.parse(data),
})
